import { useEffect, useState } from "react";
import { Alert, Modal, Button, Form } from "react-bootstrap";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService.ts";
import { EngagementService } from "@services/engagements/engagementService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { launchActions } from "src/modules/engagement/launchStore";
import type { PopulationSegment } from "@models/populationSegment.model";

interface CreateAudienceModalProps {
  show: boolean;
  onClose: () => void;
  engagementId: number;
}

/*
    An audience is created on the engagement — no campaign is chosen here.

    It does still need to know which population it slices, because that is what a
    roster is drawn from. Almost always there is only one, and then nothing is
    asked. The population field appears only when the engagement's campaigns
    target different populations, which is the one case where the answer cannot
    be inferred. The question is about the population, never about a campaign.
*/
export const CreateAudienceModal = ({ show, onClose, engagementId }: CreateAudienceModalProps) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<PopulationSegment[]>([]);
  const [segmentId, setSegmentId] = useState("");

  useEffect(() => {
    if (!show || !engagementId) return;
    let cancelled = false;

    const loadPopulationChoices = async () => {
      const campaigns = await new EngagementService().getEngagementLaunches(engagementId);
      const targeted = new Set(
        campaigns.map((c) => c.population_segment_id).filter((id): id is number => id != null),
      );
      // One population (or none recorded yet) leaves nothing to disambiguate.
      if (cancelled || targeted.size < 2) return;

      const clientId = engagementStore.selectedEngagement?.client?.id;
      if (!clientId) return;
      const segments = await new OrganizationService().getPopulationSegments(clientId);
      if (cancelled) return;
      setChoices(segments.filter((segment) => targeted.has(segment.id)));
    };

    loadPopulationChoices().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [show, engagementId]);

  const needsPopulation = choices.length > 1;

  const handleCreate = async () => {
    if (!name.trim()) return;
    if (needsPopulation && !segmentId) return;

    setSaving(true);
    setError(null);
    try {
      await new AudienceDefinitionService().createAudience(engagementId, {
        name: name.trim(),
        rules: {},
        ...(segmentId ? { population_segment_id: Number(segmentId) } : {}),
      });

      launchActions.triggerAudienceRefresh();
      setName("");
      setSegmentId("");
      onClose();
    } catch (err: any) {
      setError(err?.message ?? "Could not create the audience. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered className="tier-2-modal"
  backdropClassName="tier-2-backdrop">
      <Modal.Header closeButton>
        <Modal.Title>Create Audience Definition</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2 small">
            {error}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Audience Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Alumni 2024"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">
              You can configure targeting rules after creation.
            </Form.Text>
          </Form.Group>

          {needsPopulation && (
            <Form.Group className="mb-3">
              <Form.Label>Population</Form.Label>
              <Form.Select value={segmentId} onChange={(e) => setSegmentId(e.target.value)}>
                <option value="">Select a population</option>
                {choices.map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                This engagement's campaigns target more than one population, so this audience
                needs to say which one it is drawn from.
              </Form.Text>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={!name.trim() || (needsPopulation && !segmentId) || saving}
        >
          {saving ? "Creating..." : "Create Audience"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
